import { useEffect, useState } from 'react';

import {
  Column,
  DataPopper,
  DataPopperRow,
  Icons,
  RatioProgressBar,
  TableHeader,
  LinkBehaviour,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';
import { BestPracticeCheck } from '@cast/types';
import { clearQueryParamSilently, useSearchParamState } from '@cast/utils';

import { DataTooltip } from '@cast/components-data-tooltip';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { TourStep } from 'core/tour';
import {
  CheckType,
  SeverityChip,
  severitySortWeight,
  severityToTextMap,
} from 'features/security-insights';

import { complianceTourSteps } from '../../_tours/complianceTour';
import { checkDetailsDrawerUrlKey } from '../../constants';
import {
  TabValue,
  useCheckDetailsDrawer,
} from '../_hooks/useCheckDetailsDrawer';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
  useSearchContext,
  useSearchCriterion,
} from '@cast/shared-search-ui';

type Props = {
  clusterIds?: string[];
  isWorkloadView?: boolean;
  tableActions?: React.ReactNode;
};

export const ChecksTable = ({
  clusterIds,
  isWorkloadView,
  tableActions,
}: Props) => {
  const { value: selectedClusters } = useSearchCriterion('clusterIds');
  const { value: selectedNamespaces } = useSearchCriterion('namespaces');
  const { value: selectedStandard } = useSearchCriterion('standard');
  const { value: selectedLabels } = useSearchCriterion('labels');
  const searchCtx = useSearchContext<BestPracticeCheck>();
  const { state: ruleId, setState: setRuleId } = useSearchParamState(
    checkDetailsDrawerUrlKey
  );
  const [tab, setTab] = useState<TabValue>(TabValue.OVERVIEW);
  const { open } = useCheckDetailsDrawer(() => {
    clearQueryParamSilently(checkDetailsDrawerUrlKey);
    setRuleId(undefined);
  });

  useEffect(() => {
    if (ruleId) {
      open({
        ruleId,
        clusterIds: selectedClusters?.length ? undefined : clusterIds,
        selectedClusters,
        selectedNamespaces,
        selectedLabels,
        tab,
        selectedStandard,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId]);

  const openCheckDetailsDrawer = (check: BestPracticeCheck, tab?: TabValue) => {
    if (check.ruleId) {
      setRuleId(check.ruleId);
    }
    if (tab) {
      setTab(tab);
    }
  };

  return (
    <TourStep {...complianceTourSteps.complianceResults}>
      <Box className="tour-highlight" flexGrow={1} data-hc="success">
        <SearchableTable
          cacheKey={
            isWorkloadView
              ? tableCacheKeys.SECURITY.WORKLOAD_CHECKS
              : tableCacheKeys.SECURITY.CHECKS
          }
          testId="checks-table"
          rowKey={(row: BestPracticeCheck) => row.ruleId!}
          maxHeight="calc(100vh - 155px)"
          outerHeader={
            <TableHeader
              recordsCount={<SearchableTableRecordsCount title="checks" />}
              actions={tableActions}
            />
          }
          secondarySort={[
            { accessor: 'failed', direction: 'desc' },
            { accessor: 'severityScore', direction: 'desc' },
          ]}
          components={{
            noResults: <NoResults entity="check" />,
            noData: <NoData entity="check" />,
            failed: (
              <FailedToLoad
                title="Failed to load checks"
                refresh={searchCtx?.refetch}
              />
            ),
          }}
        >
          <Column
            id="type"
            header="Type"
            renderCell={({ manual }: BestPracticeCheck) => {
              return <CheckType type={manual ? 'manual' : 'automatic'} />;
            }}
            minWidth={68}
            cellWrapperSx={{ pl: 12, pr: 4 }}
            sortable
            accessor={({ manual }) => (manual ? 'manual' : 'automatic')}
          />
          <Column
            id="ruleId"
            header="ID"
            minWidth={60}
            cellWrapperSx={{ pl: 8, pr: 4 }}
            sortable
          />
          <Column
            id="name"
            header="Check"
            renderCell={(check: BestPracticeCheck) => {
              const { name, ruleId } = check;
              return (
                <LinkBehaviour
                  variant="P12M"
                  color={ruleId ? 'blue.500' : 'inherit'}
                  noWrap
                  onClick={() => openCheckDetailsDrawer(check)}
                >
                  {name}
                </LinkBehaviour>
              );
            }}
            minWidth={377}
            cellWrapperSx={{ pl: 8 }}
            resizable
            sortable
          />
          <Column
            id="standard"
            header="Standard"
            infoTooltip="Security standard, benchmark or area that includes the security check."
            renderCell={({ labels }: BestPracticeCheck) => {
              if (!labels?.length) {
                return (
                  <Typography component="span" variant="inherit">
                    -
                  </Typography>
                );
              }

              return (
                <Stack direction="row">
                  <Typography variant="inherit" noWrap>
                    {labels[0]}
                  </Typography>

                  {labels.length > 1 ? (
                    <Typography variant="inherit" fontWeight="bold">
                      + {labels.length - 1} more
                    </Typography>
                  ) : null}
                </Stack>
              );
            }}
            minWidth={145}
            cellWrapperSx={{ pl: 12, pr: 4 }}
            accessor={({ labels }) => labels?.[0] || -1}
            sortable
          />
          {!isWorkloadView && (
            <Column
              id="clustersCount"
              accessor="clustersCount"
              header={<Icons.Hexagon width={16} height={16} />}
              headerFullName={
                <Stack direction="row" alignItems="center" gap={4}>
                  <Icons.Hexagon width={16} height={16} /> Clusters
                </Stack>
              }
              headerCellWrapperSx={{
                '& .DS-Table-HeaderTitle': {
                  display: 'flex',
                },
              }}
              minWidth={60}
              cellWrapperSx={{ px: 8 }}
              sortable
              sortingType="number"
            />
          )}
          <Column
            id="affected"
            header="affected resources"
            renderCell={({
              total,
              failed,
              manual,
              excepted,
            }: BestPracticeCheck) => {
              return (
                <Stack flexDirection="row" width="100%" alignItems="center">
                  <Box component="div" minWidth="72px" mr={12}>
                    <Typography
                      variant="inherit"
                      color={manual ? 'grey.400' : 'red.500'}
                      component="span"
                    >
                      {failed}
                    </Typography>
                    /{total}
                  </Box>
                  <DataTooltip
                    title={
                      <DataPopper>
                        <DataPopperRow
                          indicator="red.300"
                          left="Affected"
                          right={failed}
                        />
                        <DataPopperRow
                          indicator="grey.500"
                          left="Excepted"
                          right={excepted}
                        />
                        <DataPopperRow
                          indicator="grey.200"
                          left="All"
                          right={total}
                        />
                      </DataPopper>
                    }
                  >
                    <Box
                      width="100%"
                      data-testid="affected-resources-progress-bar"
                    >
                      <RatioProgressBar
                        hideBorders
                        sx={{
                          flexGrow: 1,
                        }}
                        total={total}
                        bars={[
                          {
                            value: failed || 0,
                            color: manual ? 'grey.300' : 'red.300',
                          },
                          {
                            value: excepted || 0,
                            color: 'grey.500',
                          },
                        ]}
                      />
                    </Box>
                  </DataTooltip>
                </Stack>
              );
            }}
            bodyCellSx={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}
            minWidth={230}
            cellWrapperSx={{ pl: 12, pr: 8 }}
            accessor="failed"
            sortable
          />

          <Column
            id="severity"
            header="severity"
            renderCell={({ severityLevel }: BestPracticeCheck) => {
              return (
                <SeverityChip className={severityLevel}>
                  {severityLevel && severityLevel in severityToTextMap
                    ? severityToTextMap[severityLevel]
                    : severityLevel}
                </SeverityChip>
              );
            }}
            accessor={({ severityLevel }) =>
              severityLevel && severityLevel in severitySortWeight
                ? severitySortWeight[severityLevel]
                : -1
            }
            sortable
            defaultSort="desc"
            width={120}
          />
        </SearchableTable>
      </Box>
    </TourStep>
  );
};
